import React, { useState, useEffect } from "react";
import { X, Mail, Phone, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { login as googleLogin } from "@/lib/auth";
import { toast } from "sonner";

const MODES = {
  CHOOSE: "choose",           // pick auth method
  GOOGLE: "google",
  PASSWORD: "password",       // mobile + password login
  OTP_MOBILE: "otp_mobile",   // mobile OTP: enter mobile
  OTP_VERIFY: "otp_verify",   // OTP: enter code
  SET_PASSWORD: "set_pw",     // after OTP verify, first-time setup
  USER_TYPE: "user_type",     // after signup, ask buyer/seller/both
  FORGOT_MOBILE: "forgot_mobile",
  FORGOT_OTP: "forgot_otp",
};

const MODE_TITLES = {
  [MODES.SET_PASSWORD]: "Set your password",
  [MODES.USER_TYPE]: "One last step",
  [MODES.FORGOT_MOBILE]: "Reset password",
  [MODES.FORGOT_OTP]: "Reset password",
  [MODES.CHOOSE]: "Sign up / Sign in",
};

const INPUT_CLS = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";

/** All auth-flow state + submission handlers, shared by the mode views. */
function useLoginFlow(onClose) {
  const [mode, setMode] = useState(MODES.CHOOSE);
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setMode(MODES.CHOOSE); setMobile(""); setPassword(""); setConfirm(""); setOtp(""); setDevOtp(""); onClose(); };
  const cleanMobile = () => mobile.replace(/\D/g, "");

  const routeAfterAuth = async (successMsg) => {
    const me = await api.get("/me/profile");
    if (!me.data.user_type) { setMode(MODES.USER_TYPE); return false; }
    if (successMsg) toast.success(successMsg);
    close();
    window.location.reload();
    return true;
  };

  const sendOtp = async (forReset = false) => {
    const m = cleanMobile();
    if (m.length < 10) { toast.error("Enter valid 10-digit mobile"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/send", { mobile: m });
      if (r.data.dev_otp) setDevOtp(r.data.dev_otp);
      toast.success("OTP sent" + (r.data.dev_otp ? ` (dev: ${r.data.dev_otp})` : ""));
      setMode(forReset ? MODES.FORGOT_OTP : MODES.OTP_VERIFY);
    } catch { toast.error("Failed to send OTP"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post("/auth/otp/verify", { mobile: cleanMobile(), otp });
      const me = await api.get("/me/profile");
      if (!me.data.user_type) { setMode(MODES.USER_TYPE); return; }
      if (me.data.has_password) {
        toast.success("Signed in");
        close();
        window.location.reload();
      } else {
        setMode(MODES.SET_PASSWORD);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
    finally { setBusy(false); }
  };

  const passwordLogin = async () => {
    if (cleanMobile().length < 10) { toast.error("Enter valid mobile"); return; }
    if (!password) { toast.error("Enter password"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-login", { mobile: cleanMobile(), password });
      await routeAfterAuth("Signed in");
    } catch (e) { toast.error(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const setInitialPassword = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { password });
      await routeAfterAuth("Password set. You are signed in.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveUserType = async (t) => {
    setBusy(true);
    try {
      await api.post("/me/user-type", { user_type: t });
      toast.success("Welcome to Tharlo!");
      close();
      // Route to the right dashboard
      window.location.href = t === "buyer" ? "/dashboard" : "/broker";
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resetPassword = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { mobile: cleanMobile(), otp, new_password: password });
      toast.success("Password reset. You are signed in.");
      close(); window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const skipPassword = () => { close(); window.location.reload(); };

  return {
    mode, setMode, mobile, setMobile, password, setPassword, confirm, setConfirm,
    otp, setOtp, devOtp, busy, close,
    sendOtp, verifyOtp, passwordLogin, setInitialPassword, saveUserType, resetPassword, skipPassword,
  };
}

function MobileInput({ value, onChange, testId }) {
  return (
    <div className="flex gap-2">
      <span className={`${INPUT_CLS} !w-auto text-muted-foreground bg-muted`}>+91</span>
      <input data-testid={testId} className={INPUT_CLS} placeholder="98765 43210" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function DevOtpHint({ devOtp }) {
  if (!devOtp) return null;
  return <div className="text-xs text-gold text-center bg-gold/10 border border-gold/30 p-2">Dev OTP: <strong>{devOtp}</strong></div>;
}

function ChooseMethod({ flow }) {
  return (
    <div className="space-y-2.5">
      <p className="text-xs text-muted-foreground mb-3">Choose how you'd like to sign up or sign in:</p>
      <button onClick={() => flow.setMode(MODES.PASSWORD)} data-testid="login-choose-password" className="w-full flex items-center gap-3 border border-border hover:border-ink p-3 text-left">
        <KeyRound className="w-5 h-5 text-gold shrink-0" />
        <div>
          <div className="font-semibold text-ink text-sm">Sign in with Password</div>
          <div className="text-xs text-muted-foreground">Mobile + password</div>
        </div>
      </button>
      <button onClick={() => flow.setMode(MODES.OTP_MOBILE)} data-testid="login-choose-otp" className="w-full flex items-center gap-3 border border-border hover:border-ink p-3 text-left">
        <Phone className="w-5 h-5 text-gold shrink-0" />
        <div>
          <div className="font-semibold text-ink text-sm">Sign up / in with Mobile OTP</div>
          <div className="text-xs text-muted-foreground">One-time code via SMS</div>
        </div>
      </button>
      <button onClick={googleLogin} data-testid="login-choose-google" className="w-full flex items-center gap-3 border border-border hover:border-ink p-3 text-left">
        <Mail className="w-5 h-5 text-gold shrink-0" />
        <div>
          <div className="font-semibold text-ink text-sm">Continue with Google</div>
          <div className="text-xs text-muted-foreground">One-click, no password</div>
        </div>
      </button>
      <button onClick={flow.close} data-testid="login-continue-browsing" className="w-full text-center pt-3 mt-2 text-sm text-muted-foreground hover:text-ink border-t border-border">
        ← Continue browsing without signing in
      </button>
    </div>
  );
}

function PasswordLoginForm({ flow }) {
  return (
    <div className="space-y-3">
      <label className="text-xs uppercase tracking-widest text-muted-foreground">Mobile Number</label>
      <MobileInput value={flow.mobile} onChange={flow.setMobile} testId="login-pw-mobile" />
      <label className="text-xs uppercase tracking-widest text-muted-foreground">Password</label>
      <input data-testid="login-pw-password" type="password" className={INPUT_CLS} placeholder="Enter your password" value={flow.password} onChange={(e) => flow.setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && flow.passwordLogin()} />
      <button onClick={flow.passwordLogin} disabled={flow.busy} data-testid="login-pw-submit" className="btn-primary w-full">{flow.busy ? "Signing in..." : "Sign In"}</button>
      <div className="flex items-center justify-between text-xs">
        <button onClick={() => flow.setMode(MODES.FORGOT_MOBILE)} className="text-gold hover:underline" data-testid="login-forgot">Forgot password?</button>
        <button onClick={() => flow.setMode(MODES.CHOOSE)} className="text-muted-foreground hover:text-ink">← Other options</button>
      </div>
    </div>
  );
}

function OtpMobileForm({ flow }) {
  return (
    <div className="space-y-3">
      <label className="text-xs uppercase tracking-widest text-muted-foreground">Mobile Number</label>
      <MobileInput value={flow.mobile} onChange={flow.setMobile} testId="login-mobile" />
      <button onClick={() => flow.sendOtp(false)} disabled={flow.busy} data-testid="login-send-otp" className="btn-gold w-full">{flow.busy ? "Sending..." : "Send OTP"}</button>
      <p className="text-[11px] text-muted-foreground text-center">SMS not integrated — OTP will display on-screen.</p>
      <button onClick={() => flow.setMode(MODES.CHOOSE)} className="text-xs text-muted-foreground hover:text-ink w-full text-center">← Other options</button>
    </div>
  );
}

function OtpVerifyForm({ flow }) {
  return (
    <div className="space-y-3">
      <label className="text-xs uppercase tracking-widest text-muted-foreground">Enter 6-digit OTP</label>
      <input data-testid="login-otp" className={INPUT_CLS + " text-center text-2xl tracking-widest font-mono"} maxLength={6} placeholder="000000" value={flow.otp} onChange={(e) => flow.setOtp(e.target.value)} />
      <DevOtpHint devOtp={flow.devOtp} />
      <button onClick={flow.verifyOtp} disabled={flow.busy || flow.otp.length !== 6} data-testid="login-verify-otp" className="btn-primary w-full">{flow.busy ? "Verifying..." : "Verify & Sign In"}</button>
      <button onClick={() => flow.setMode(MODES.OTP_MOBILE)} className="text-xs text-muted-foreground hover:text-ink w-full text-center">← Change number</button>
    </div>
  );
}

function SetPasswordForm({ flow }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Set a password so you can sign in without OTP next time.</p>
      <input data-testid="setpw-new" type="password" className={INPUT_CLS} placeholder="New password (min 8 chars)" value={flow.password} onChange={(e) => flow.setPassword(e.target.value)} />
      <input data-testid="setpw-confirm" type="password" className={INPUT_CLS} placeholder="Confirm password" value={flow.confirm} onChange={(e) => flow.setConfirm(e.target.value)} />
      <button onClick={flow.setInitialPassword} disabled={flow.busy} data-testid="setpw-submit" className="btn-primary w-full">{flow.busy ? "Setting..." : "Set Password & Continue"}</button>
      <button onClick={flow.skipPassword} data-testid="setpw-skip" className="text-xs text-muted-foreground hover:text-ink w-full text-center">Skip for now (I'll continue with OTP each time)</button>
    </div>
  );
}

const USER_TYPE_OPTIONS = [
  { type: "buyer", badge: "B", badgeCls: "bg-gold/10 text-gold", title: "I'm a Buyer", desc: "Browse verified properties, save favourites, post requirements" },
  { type: "seller", badge: "S", badgeCls: "bg-gold/10 text-gold", title: "I'm a Seller / Broker / Developer", desc: "List properties, manage leads, get Tharlo verified" },
  { type: "both", badge: "B+S", badgeCls: "bg-ink text-gold", title: "Both — I buy and sell", desc: "Full access to buyer & seller dashboards" },
];

function UserTypePicker({ flow }) {
  return (
    <div className="space-y-3" data-testid="user-type-picker">
      <p className="text-sm text-muted-foreground">How will you use Tharlo?</p>
      {USER_TYPE_OPTIONS.map((opt) => (
        <button
          key={opt.type}
          onClick={() => flow.saveUserType(opt.type)}
          disabled={flow.busy}
          data-testid={`user-type-${opt.type}`}
          className="w-full flex items-start gap-3 border border-border hover:border-ink p-4 text-left"
        >
          <div className={`w-8 h-8 ${opt.badgeCls} flex items-center justify-center shrink-0 font-bold`}>{opt.badge}</div>
          <div>
            <div className="font-semibold text-ink text-sm">{opt.title}</div>
            <div className="text-xs text-muted-foreground">{opt.desc}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ForgotMobileForm({ flow }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Enter your registered mobile. We'll send an OTP to reset your password.</p>
      <MobileInput value={flow.mobile} onChange={flow.setMobile} testId="forgot-mobile" />
      <button onClick={() => flow.sendOtp(true)} disabled={flow.busy} data-testid="forgot-send-otp" className="btn-gold w-full">{flow.busy ? "Sending..." : "Send OTP"}</button>
      <button onClick={() => flow.setMode(MODES.PASSWORD)} className="text-xs text-muted-foreground hover:text-ink w-full text-center">← Back to sign in</button>
    </div>
  );
}

function ForgotOtpForm({ flow }) {
  return (
    <div className="space-y-3">
      <label className="text-xs uppercase tracking-widest text-muted-foreground">OTP</label>
      <input data-testid="forgot-otp" className={INPUT_CLS + " text-center text-lg tracking-widest font-mono"} maxLength={6} placeholder="000000" value={flow.otp} onChange={(e) => flow.setOtp(e.target.value)} />
      <DevOtpHint devOtp={flow.devOtp} />
      <input data-testid="forgot-newpw" type="password" className={INPUT_CLS} placeholder="New password (min 8)" value={flow.password} onChange={(e) => flow.setPassword(e.target.value)} />
      <input data-testid="forgot-confirm" type="password" className={INPUT_CLS} placeholder="Confirm password" value={flow.confirm} onChange={(e) => flow.setConfirm(e.target.value)} />
      <button onClick={flow.resetPassword} disabled={flow.busy} data-testid="forgot-submit" className="btn-primary w-full">{flow.busy ? "Resetting..." : "Reset Password"}</button>
      <button onClick={() => flow.setMode(MODES.FORGOT_MOBILE)} className="text-xs text-muted-foreground hover:text-ink w-full text-center">← Resend OTP</button>
    </div>
  );
}

const MODE_VIEWS = {
  [MODES.CHOOSE]: ChooseMethod,
  [MODES.PASSWORD]: PasswordLoginForm,
  [MODES.OTP_MOBILE]: OtpMobileForm,
  [MODES.OTP_VERIFY]: OtpVerifyForm,
  [MODES.SET_PASSWORD]: SetPasswordForm,
  [MODES.USER_TYPE]: UserTypePicker,
  [MODES.FORGOT_MOBILE]: ForgotMobileForm,
  [MODES.FORGOT_OTP]: ForgotOtpForm,
};

export default function LoginModal({ open, onClose }) {
  const flow = useLoginFlow(onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ModeView = MODE_VIEWS[flow.mode] || ChooseMethod;
  const title = MODE_TITLES[flow.mode] || "Sign in to continue";

  return (
    <div className="fixed inset-0 z-[100] bg-ink/70 backdrop-blur-sm flex items-start justify-center p-4 pt-6 md:pt-10 overflow-y-auto" data-testid="login-modal" onClick={flow.close}>
      <div className="bg-white w-full max-w-md border border-border mb-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white">
          <div>
            <div className="overline">Welcome to Tharlo</div>
            <h2 className="font-display text-xl text-ink mt-0.5">{title}</h2>
          </div>
          <button onClick={flow.close} data-testid="login-close" className="p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          <ModeView flow={flow} />
        </div>
      </div>
    </div>
  );
}
